import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../core/services/api.service';
import {
  ShoppingPartnerRewardAggregator,
  ShoppingPartnerRewardRow,
  ShoppingPartnerRewardUpload,
} from '../../core/models/api.models';

@Component({
  selector: 'app-shopping-partner-rewards',
  standalone: true,
  imports: [FormsModule, DatePipe],
  templateUrl: './shopping-partner-rewards.component.html',
  styleUrl: './shopping-partner-rewards.component.scss',
})
export class ShoppingPartnerRewardsComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly loading = signal(false);
  readonly uploading = signal(false);
  readonly detailLoading = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly aggregators = signal<ShoppingPartnerRewardAggregator[]>([]);
  readonly uploads = signal<ShoppingPartnerRewardUpload[]>([]);
  readonly page = signal(1);
  readonly total = signal(0);
  readonly limit = 20;

  readonly selectedUpload = signal<ShoppingPartnerRewardUpload | null>(null);
  readonly detailRows = signal<ShoppingPartnerRewardRow[]>([]);
  readonly detailPage = signal(1);
  readonly detailTotal = signal(0);
  readonly detailLimit = 50;

  aggregator = 'impact';
  filterAggregator = '';
  filterStatus = '';
  detailStatus = '';
  selectedFile: File | null = null;

  ngOnInit(): void {
    this.loadAggregators();
    this.load();
  }

  loadAggregators(): void {
    this.api.getShoppingPartnerRewardAggregators().subscribe({
      next: (data) => {
        const list = data?.aggregators || [];
        this.aggregators.set(list);
        if (list.length && !list.some((a) => a.key === this.aggregator)) {
          this.aggregator = list[0].key;
        }
      },
      error: () => this.aggregators.set([{ key: 'impact', label: 'IMPACT' }]),
    });
  }

  load(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.page.set(page);
    const filters: { aggregator?: string; status?: string } = {};
    if (this.filterAggregator) filters.aggregator = this.filterAggregator;
    if (this.filterStatus) filters.status = this.filterStatus;

    this.api.getShoppingPartnerRewardUploads(page, this.limit, filters).subscribe({
      next: (data) => {
        this.uploads.set(data?.rows || []);
        this.total.set(data?.total || 0);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load uploads');
        this.loading.set(false);
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] || null;
  }

  upload(): void {
    if (!this.selectedFile || this.uploading()) return;
    if (!this.aggregator) {
      this.error.set('Select an aggregator');
      return;
    }
    this.uploading.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.uploadShoppingPartnerRewardCsv(this.aggregator, this.selectedFile).subscribe({
      next: (upload) => {
        this.uploading.set(false);
        this.selectedFile = null;
        this.success.set(
          `Processed ${upload.total_rows} rows — ${upload.provided_count} provided, ${upload.failed_count} failed`
        );
        this.load(1);
        this.openDetail(upload);
      },
      error: (err: { message?: string }) => {
        this.uploading.set(false);
        this.error.set(err?.message || 'Upload failed');
      },
    });
  }

  openDetail(upload: ShoppingPartnerRewardUpload): void {
    this.selectedUpload.set(upload);
    this.detailStatus = '';
    this.loadDetail(1);
  }

  closeDetail(): void {
    this.selectedUpload.set(null);
    this.detailRows.set([]);
  }

  loadDetail(page = 1): void {
    const upload = this.selectedUpload();
    if (!upload) return;
    this.detailLoading.set(true);
    this.detailPage.set(page);
    this.api
      .getShoppingPartnerRewardUpload(
        upload.id,
        page,
        this.detailLimit,
        this.detailStatus || undefined
      )
      .subscribe({
        next: (data) => {
          this.selectedUpload.set(data.upload);
          this.detailRows.set(data.rows?.rows || []);
          this.detailTotal.set(data.rows?.total || 0);
          this.detailLoading.set(false);
        },
        error: (err: { message?: string }) => {
          this.error.set(err?.message || 'Failed to load upload detail');
          this.detailLoading.set(false);
        },
      });
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit));
  }

  detailTotalPages(): number {
    return Math.max(1, Math.ceil(this.detailTotal() / this.detailLimit));
  }
}
